'use client';

import { useCallback, useEffect, useState } from 'react';

import { config } from '@ps/config/client';
import {
  FlowChannels,
  subscribe,
} from '@ps/features/flow-registry/flow-events';
import { auth } from '@ps/functions/firebase/auth';
import type {
  PopStoreSubscription,
  Subscriptions,
} from '@ps/models/lib/creator-subscription';

import { subscriptionsToList } from '../functions/access-control';

export async function fetchCreatorSubscriptions(
  creatorId: string,
): Promise<Subscriptions> {
  const token = await auth().currentUser?.getIdToken();
  if (!token) throw new Error('User is not authenticated');
  const url = `${config.awsCreatorApiBaseUrl}/fetchAllSubscriptions?creator_store_id=${encodeURIComponent(
    creatorId,
  )}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: token, 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch subscriptions: ${response.status}`);
  }
  return (await response.json()) as Subscriptions;
}

export type RefreshSubscriptionsCallback = (
  error: unknown | undefined,
  subscriptionList: PopStoreSubscription[],
) => void;

export type RefreshSubscriptions = (options?: {
  callback?: RefreshSubscriptionsCallback;
}) => Promise<void>;

/**
 * Loads the creator's full `Subscriptions` tier set. Mirrors
 * PopshopRN's `useFetchAllSubscriptions`: keyed-by-level container +
 * refresh-with-callback contract so the `SubscriptionLevelPicker`
 * can re-check the configuration state after a deep-link round-trip.
 *
 * Web port note: PopshopRN re-fetched on screen focus via
 * `useFocusEffect`. The web equivalent is the document visibility
 * event, but the picker drives its own refresh through the callback
 * so we skip the focus rehydrate here.
 */
export function useFetchAllSubscriptions(creatorId: string | undefined) {
  const [subscriptions, setSubscriptions] = useState<Subscriptions>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(false);

  const refresh: RefreshSubscriptions = useCallback(
    async options => {
      if (!creatorId) return;
      try {
        setLoading(true);
        const next = await fetchCreatorSubscriptions(creatorId);
        const list = subscriptionsToList(next);
        setSubscriptions(next);
        setError(undefined);
        options?.callback?.(undefined, list);
      } catch (fetchError) {
        setError(fetchError);
        options?.callback?.(fetchError, []);
      } finally {
        setLoading(false);
      }
    },
    [creatorId],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Live-update when the creator saves a tier in the Configure
  // Subscriptions surface. Consumers of this hook (the course form's
  // access settings, tier pickers, …) are typically mounted BEHIND
  // that modal, so without this signal they'd keep the tier list they
  // fetched at mount and never show the just-configured tier.
  useEffect(() => {
    return subscribe(FlowChannels.SubscriptionConfigSaved, () => {
      void refresh();
    });
  }, [refresh]);

  return { subscriptions, error, loading, refresh };
}
